<script lang="ts" setup>
const emit = defineEmits(["toSupport", "continue", "skip", "back"]);
const userStore = useUserStore();
const route = useRoute();
const { $serverApi } = useNuxtApp();
const methods = ref([
  {
    name: "main.modalUserWalletCreate.socialTitle",
    id: "google" as Extract<UserKeyMethod, "google">,
    description: "main.modalUserWalletCreate.socialDescription",
    icon: "mdi:google",
    enabled: userStore.keys?.google != null,
  },
  {
    name: "main.modalUserWalletCreate.socialTitle",
    id: "apple" as Extract<UserKeyMethod, "apple">,
    description: "main.modalUserWalletCreate.socialDescription",
    icon: "mdi:apple",
    enabled: userStore.keys?.apple != null,
  },
  {
    name: "main.modalUserWalletCreate.socialTitle",
    id: "facebook" as Extract<UserKeyMethod, "facebook">,
    description: "main.modalUserWalletCreate.socialDescription",
    icon: "mdi:facebook",
    enabled: userStore.keys?.facebook != null,
  },
  {
    name: "main.modalUserWalletCreate.GFALRecoveryKit",
    id: "recoveryPassword" as Extract<UserKeyMethod, "recoveryPassword">,
    description: "main.walletLock.useRecoveryKit",
    icon: "mdi:shield-key",
    enabled: userStore.keys?.recoveryPassword != null,
  },
]);

const secretInfo = ref<{ type: UserKeyMethod } | null>(null);

const getEnabledMethods = computed(() => {
  if (!secretInfo.value?.type) {
    return methods.value.filter((method) => method.enabled);
  }
  return methods.value.filter(
    (method) => method.enabled && method.id !== secretInfo.value?.type
  );
});

const processingLogin = ref<Extract<
  UserKeyMethod,
  "google" | "apple" | "facebook" | "recoveryPassword"
> | null>(null);

const handleSelectedMethod = async (
  methodId: Extract<
    UserKeyMethod,
    "google" | "apple" | "facebook" | "recoveryPassword"
  >
) => {
  processingLogin.value = methodId;
  if (
    methodId === "google" ||
    methodId === "apple" ||
    methodId === "facebook"
  ) {
    const currentPath = getCurrentFullPath(route.path);
    const requestBody = {
      methodId,
      path: currentPath,
    };
    const headers = {
      Authorization: `Bearer ${useUserStore().authToken}`,
    };
    let response: any;
    try {
      response = await $serverApi(`/oauth/existing-link/`, {
        method: "POST",
        headers,
        body: requestBody,
      });
    } catch {
      response = await $fetch(`/oauth/existing-link/`, {
        method: "POST",
        headers,
        body: requestBody,
      });
    }
    if (response.statusCode !== 200)
      throw new Error("Failed to get oauth link");
    else navigateTo(response.payload, { external: true });
  } else if (methodId === "recoveryPassword") {
    emit("skip", {
      method: "recoveryPassword",
      value: "",
    });
  }
};

onMounted(async () => {
  try {
    const gfalid = useCookie(`gfalid-${useUserStore().id}`);
    if (!gfalid.value) throw new Error("Missing secret");
    secretInfo.value = await parseSecret(gfalid.value);
  } catch (error) {
    console.error("Could not determine current secret type", error);
  }
  if (getEnabledMethods.value.length === 0) emit("back");
});
</script>

<template>
  <div class="mt-6 mb-2 flex flex-col items-start gap-4">
    <p class="text-neutral-300">
      {{ $t("main.walletLock.unlockWithChosenRecovery") }}
    </p>
    <div class="flex flex-col items-start gap-2 w-full">
      <div
        v-for="method in getEnabledMethods"
        :key="method.id"
        @click="handleSelectedMethod(method.id)"
        class="group flex flex-col gap-4 bg-neutral-500 rounded-xl p-4 w-full text-neutral-300 cursor-pointer hover:text-primary-500">
        <div class="flex items-center justify-between gap-4">
          <NuxtIcon :name="method.icon" class="w-7 h-7" />
          <div class="flex-1 flex flex-col">
            <p class="font-bold text-neutral-200 group-hover:text-primary-500">
              {{ $t(method.name, { social: $t("main." + method.id) }) }}
            </p>
            <p class="text-sm">
              {{ $t(method.description, { social: $t("main." + method.id) }) }}
            </p>
          </div>
          <NuxtIcon
            v-if="processingLogin !== method.id"
            name="mdi:external-link"
            class="w-6 h-6" />
          <LoaderSpinner v-else />
        </div>
      </div>
    </div>
    <div class="mt-4 flex justify-between items-center w-full">
      <button
        @click="emit('back')"
        class="text-sm text-neutral-300 cursor-pointer">
        <
        {{ $t("main.walletLock.back") }}
      </button>
      <button
        @click="emit('toSupport')"
        class="text-sm text-primary-500 hover:text-error-300 cursor-pointer underline">
        {{ $t("main.walletLock.noAccessToRecoveryMethods") }}
      </button>
    </div>
  </div>
</template>
